import React, { useState, useEffect } from "react";
import { PageComponent } from "../../types/components";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { fetchSheetNames } from "../../services/google-sheets.service";
import { useIntegrationsStore } from "../../stores/integrations.store";
import { useWorkspaceStore } from "../../stores/workspace.store";
import { useAuthStore } from "../../stores/auth.store";
import { integrationsApi } from "../../services/integrations.service";
import { useQuery } from "@tanstack/react-query";
import { MetricCardConfig } from "./config-fields/MetricCardConfig";
import { ChartConfig } from "./config-fields/ChartConfig";
import { TableConfig } from "./config-fields/TableConfig";
import { RelationshipTableConfig } from "./config-fields/RelationshipTableConfig";
import { FilterConfig } from "./config-fields/FilterConfig";
import { CalendarConfig } from "./config-fields/CalendarConfig";
import { GanttConfig } from "./config-fields/GanttConfig";
import { ContainerConfig } from "./config-fields/ContainerConfig";

interface ComponentEditModalProps {
  component: PageComponent | null;
  onClose: () => void;
  onSave: (component: PageComponent) => void;
  availableComponents?: PageComponent[];
  pageComponents?: PageComponent[];
}

export default function ComponentEditModal({
  component,
  onClose,
  onSave,
  availableComponents = [],
  pageComponents = [],
}: ComponentEditModalProps) {
  const { currentWorkspace } = useWorkspaceStore();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const { integrations: storeIntegrations, setIntegrations } = useIntegrationsStore();

  // Ensure integrations are loaded
  const { data: integrations = storeIntegrations } = useQuery({
    queryKey: ["integrations", currentWorkspace?.id],
    queryFn: () => integrationsApi.getAll(currentWorkspace!.id),
    enabled: !!currentWorkspace,
    initialData: storeIntegrations.length > 0 ? storeIntegrations : undefined,
  });

  // Sync to store when freshly fetched
  useEffect(() => {
    if (integrations.length > 0) setIntegrations(integrations);
  }, [integrations]);

  const sheetsIntegrations = integrations.filter((i) => i.type === "google_sheets");
  const calendarIntegrations = integrations.filter((i) => i.type === "google_calendar");
  const restApiIntegrations = integrations.filter((i) => i.type === "rest_api");

  const [editedComponent, setEditedComponent] = useState<PageComponent | null>(
    null,
  );
  const [dataSourceType, setDataSourceType] = useState<"manual" | "component" | "googleSheets" | "dynamic">(
    "manual",
  );
  const [uploadMode, setUploadMode] = useState<"replace" | "add">("replace");
  const [tableDataSource, setTableDataSource] = useState<"manual" | "googleSheets" | "excel" | "restApi">("manual");
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [sheetFetchError, setSheetFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (component) {
      setEditedComponent(JSON.parse(JSON.stringify(component)));
      
      // Detectar tipo de fonte de dados
      // Google Sheets só é válido para tabelas, outros componentes usam sourceComponentId
      if (component.type === "table" && (component.config.googleSheets?.integrationId || component.config.googleSheets?.spreadsheetId)) {
        setDataSourceType("googleSheets");
      } else if (component.config.dynamicSourceField !== undefined) {
        setDataSourceType("dynamic");
      } else if (component.config.sourceComponentId) {
        setDataSourceType("component");
      } else {
        setDataSourceType("manual");
      }
      
      // Detectar fonte de dados da tabela
      if (component.type === "table") {
        if (component.config.restApi?.enabled) {
          setTableDataSource("restApi");
        } else if (component.config.googleSheets?.enabled) {
          setTableDataSource("googleSheets");
          // Load available sheets if integration is already set
          const integrationId = component.config.googleSheets?.integrationId;
          if (integrationId) {
            const integration = (storeIntegrations || []).find((i) => i.id === integrationId);
            if (integration?.config?.spreadsheetId) {
              handleFetchSheets(integration.config.spreadsheetId);
            }
          }
        } else {
          setTableDataSource("manual");
        }
      }
    }
  }, [component]);

  const updateConfig = (key: string, value: any) => {
    setEditedComponent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        config: {
          ...prev.config,
          [key]: value,
        },
      };
    });
  };

  // Função para buscar abas disponíveis
  const handleFetchSheets = async (spreadsheetId: string) => {
    if (!spreadsheetId || spreadsheetId.trim() === '') {
      setAvailableSheets([]);
      setSheetFetchError(null);
      return;
    }

    setLoadingSheets(true);
    setSheetFetchError(null);
    try {
      const sheets = await fetchSheetNames(spreadsheetId);
      setAvailableSheets(sheets);
      setSheetFetchError(null);
      
      // Se há abas disponíveis e nenhuma foi selecionada ainda, seleciona a primeira
      if (sheets.length > 0) {
        setEditedComponent((prev) => {
          if (!prev) return prev;
          // Só auto-seleciona se ainda não há range definido
          if (prev.config.googleSheets?.range) return prev;
          return {
            ...prev,
            config: {
              ...prev.config,
              googleSheets: {
                ...prev.config.googleSheets,
                range: sheets[0],
              },
            },
          };
        });
      }
    } catch (error: any) {
      console.error('Erro ao buscar abas:', error);
      setAvailableSheets([]);
      setSheetFetchError(error.message || 'Erro ao buscar planilha');
    } finally {
      setLoadingSheets(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editedComponent) return;
    onSave(editedComponent);
    onClose();
  };

  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonDataRaw = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonDataRaw.length === 0) {
          alert("O arquivo Excel está vazio!");
          return;
        }

        // Tentar encontrar cabeçalhos na primeira linha
        let headers = jsonDataRaw[0] as string[];
        let validHeaders = headers.filter(
          (h) => h && h.toString().trim() !== "",
        );
        let dataStartIndex = 1;

        // Se não houver cabeçalhos válidos no index 0, tenta no index 1
        if (validHeaders.length === 0 && jsonDataRaw.length > 1) {
          headers = jsonDataRaw[1] as string[];
          validHeaders = headers.filter((h) => h && h.toString().trim() !== "");
          dataStartIndex = 2;
        }

        if (validHeaders.length === 0) {
          alert("Nenhum cabeçalho válido encontrado no arquivo!");
          return;
        }

        // Criar dados a partir das linhas restantes (após os cabeçalhos)
        const rows = jsonDataRaw.slice(dataStartIndex) as any[][];
        const newData = rows
          .filter((row) => row && row.length > 0) // Filtrar linhas vazias
          .map((row) => {
            const rowData: any = {};
            validHeaders.forEach((header, index) => {
              const field = header
                .toString()
                .toLowerCase()
                .replace(/\s+/g, "_");
              rowData[field] = row[index] !== undefined ? row[index] : "";
            });
            // Adicionar timestamp de importação
            rowData._importado_em = new Date().toLocaleString("pt-BR");
            return rowData;
          });

        // Detectar tipo de cada coluna baseado nos dados
        const detectColumnType = (headerIndex: number): string => {
          const sampleSize = Math.min(20, newData.length); // Analisa até 20 linhas
          const samples = newData.slice(0, sampleSize);

          let numberCount = 0;
          let dateCount = 0;
          let validSamples = 0;

          samples.forEach((row) => {
            const field = validHeaders[headerIndex]
              .toString()
              .toLowerCase()
              .replace(/\s+/g, "_");
            const value = row[field];

            if (value === null || value === undefined || value === "") return;

            validSamples++;

            // Verifica se é número (mas não uma data no formato dd/mm/yyyy ou dd-mm-yyyy)
            const stringValue = value.toString().trim();
            const hasDateSeparators = /[\/-]/.test(stringValue);

            if (
              (typeof value === "number" ||
                (!isNaN(Number(value)) && stringValue !== "")) &&
              !hasDateSeparators
            ) {
              numberCount++;
              return;
            }

            // Verifica se é data no formato brasileiro (dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy)
            const datePatterns = [
              /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}$/, // dd/mm/yyyy ou dd-mm-yyyy ou dd.mm.yyyy
              /^\d{4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2}$/, // yyyy/mm/dd ou yyyy-mm-dd
            ];

            const matchesDatePattern = datePatterns.some((pattern) =>
              pattern.test(stringValue),
            );

            if (matchesDatePattern) {
              // Tenta converter para verificar se é uma data válida
              const parts = stringValue.split(/[\/\-\.]/);
              let dateToCheck: Date | null = null;

              if (parts.length === 3) {
                // Se começa com 4 dígitos, é yyyy-mm-dd
                if (parts[0].length === 4) {
                  dateToCheck = new Date(
                    parseInt(parts[0]),
                    parseInt(parts[1]) - 1,
                    parseInt(parts[2]),
                  );
                } else {
                  // Formato brasileiro dd/mm/yyyy
                  dateToCheck = new Date(
                    parseInt(parts[2]),
                    parseInt(parts[1]) - 1,
                    parseInt(parts[0]),
                  );
                }

                if (dateToCheck && !isNaN(dateToCheck.getTime())) {
                  dateCount++;
                }
              }
            }
          });

          if (validSamples === 0) return "text";

          // Se 80% ou mais dos valores são números, é number
          if (numberCount / validSamples >= 0.8) return "number";

          // Se 80% ou mais dos valores são datas, é date
          if (dateCount / validSamples >= 0.8) return "date";

          return "text";
        };

        // Criar colunas baseadas nos cabeçalhos com tipos detectados
        const newColumns = validHeaders.map((header, index) => {
          const type = detectColumnType(index);
          return {
            field: header.toString().toLowerCase().replace(/\s+/g, "_"),
            label: header.toString(),
            type: type,
            visible: type !== "id",
          };
        });

        // Adicionar coluna de data de importação
        newColumns.push({
          field: "_importado_em",
          label: "Importado em",
          type: "text",
          visible: true,
        });

        // Obter dados existentes se estiver em modo "adicionar"
        const existingData =
          uploadMode === "add"
            ? editedComponent?.config?.dataSource?.data || []
            : [];

        // Mesclar ou substituir dados
        const finalData =
          uploadMode === "add" ? [...existingData, ...newData] : newData;

        // Se estiver adicionando, mesclar colunas (evitar duplicatas)
        let finalColumns = newColumns;
        if (uploadMode === "add" && editedComponent?.config?.columns) {
          const existingColumns = editedComponent.config.columns;
          const existingFields = new Set(
            existingColumns.map((col: any) => col.field),
          );

          // Adicionar apenas colunas novas que não existem
          const columnsToAdd = newColumns.filter(
            (col) => !existingFields.has(col.field),
          );
          finalColumns = [...existingColumns, ...columnsToAdd];
        }

        // Atualizar o componente com colunas e dados
        setEditedComponent((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            config: {
              ...prev.config,
              columns: finalColumns,
              dataSource: {
                type: "static",
                data: finalData,
              },
            },
          };
        });

        const message =
          uploadMode === "add"
            ? `✅ Adicionado com sucesso! ${newData.length} novas linhas. Total: ${finalData.length} linhas.`
            : `✅ Importado com sucesso! ${newColumns.length} colunas e ${newData.length} linhas.`;
        alert(message);
      } catch (error) {
        console.error("Erro ao processar Excel:", error);
        alert("Erro ao processar o arquivo Excel. Verifique o formato.");
      }
    };
    reader.readAsBinaryString(file);

    // Limpar o input para permitir upload do mesmo arquivo novamente
    e.target.value = "";
  };

  if (!component || !editedComponent) return null;

  const sharedProps = {
    editedComponent,
    setEditedComponent,
    updateConfig,
    availableComponents,
    pageComponents,
    integrations,
    sheetsIntegrations,
    calendarIntegrations,
    restApiIntegrations,
    isSuperAdmin,
  };

  const renderFields = () => {
    switch (component.type) {
      case "metric-card":
        return <MetricCardConfig {...sharedProps} />;

      case "chart":
        return (
          <ChartConfig
            {...sharedProps}
            dataSourceType={dataSourceType}
            setDataSourceType={setDataSourceType}
          />
        );

      case "table":
        return (
          <TableConfig
            {...sharedProps}
            tableDataSource={tableDataSource}
            setTableDataSource={setTableDataSource}
            availableSheets={availableSheets}
            loadingSheets={loadingSheets}
            sheetFetchError={sheetFetchError}
            handleFetchSheets={handleFetchSheets}
            handleExcelUpload={handleExcelUpload}
            uploadMode={uploadMode}
            setUploadMode={setUploadMode}
          />
        );

      case "relationship-table":
        return <RelationshipTableConfig {...sharedProps} />;

      case "filter":
        return <FilterConfig {...sharedProps} />;

      case "calendar":
        return <CalendarConfig {...sharedProps} />;

      case "gantt":
        return <GanttConfig {...sharedProps} />;

      case "container":
        return <ContainerConfig {...sharedProps} />;

      case "list":
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="listTitle">Título</Label>
              <Input
                id="listTitle"
                type="text"
                value={editedComponent.config.title || ""}
                onChange={(e) => updateConfig("title", e.target.value)}
              />
            </div>
            <div className="p-4 bg-accent border border-primary/20 rounded-md">
              <p className="text-sm">
                <strong>Dica:</strong> Os itens da lista podem ser editados
                clicando no botão de dados que aparece no próprio componente.
              </p>
            </div>
          </>
        );

      case "kanban":
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="kanbanTitle">Título</Label>
              <Input
                id="kanbanTitle"
                type="text"
                value={editedComponent.config.title || ""}
                onChange={(e) => updateConfig("title", e.target.value)}
              />
            </div>
            <div className="p-4 bg-accent border border-primary/20 rounded-md">
              <p className="text-sm">
                <strong>Dica:</strong> As colunas e cards do Kanban podem ser
                editados clicando no botão de dados que aparece no próprio
                componente.
              </p>
            </div>
          </>
        );

      case "text":
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="textContent">Conteúdo</Label>
              <Textarea
                id="textContent"
                value={editedComponent.config.content || ""}
                onChange={(e) => updateConfig("content", e.target.value)}
                rows={6}
                placeholder="Digite seu texto aqui..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fontSize">Tamanho da Fonte</Label>
              <Input
                id="fontSize"
                type="number"
                value={editedComponent.config.fontSize || 14}
                onChange={(e) =>
                  updateConfig("fontSize", Number(e.target.value))
                }
                min="8"
                max="72"
              />
            </div>
          </>
        );

      case "divider":
        return (
          <div className="space-y-2">
            <Label htmlFor="borderColor">Cor da Borda</Label>
            <input
              id="borderColor"
              type="color"
              value={editedComponent.config.style?.borderColor || "#e5e7eb"}
              onChange={(e) =>
                updateConfig("style", {
                  ...editedComponent.config.style,
                  borderColor: e.target.value,
                })
              }
              className="w-full h-10 px-1 py-1 border rounded-md"
            />
          </div>
        );

      case "button": {
        const buttonDataComponents = availableComponents.filter(
          (c) =>
            c.id !== editedComponent.id && ["table", "list"].includes(c.type),
        );
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="buttonLabel">Texto do Botão</Label>
              <Input
                id="buttonLabel"
                type="text"
                value={editedComponent.config.label || ""}
                onChange={(e) => updateConfig("label", e.target.value)}
                placeholder="Novo Registro"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="buttonSource">Conectar a Tabela/Lista</Label>
              <Select
                value={editedComponent.config.sourceComponentId || ""}
                onValueChange={(value) =>
                  updateConfig("sourceComponentId", value || undefined)
                }
              >
                <SelectTrigger id="buttonSource">
                  <SelectValue placeholder="Selecione uma tabela ou lista" />
                </SelectTrigger>
                <SelectContent>
                  {buttonDataComponents.map((comp) => {
                    const pageName = (comp as any)._pageName;
                    const displayName = pageName
                      ? `${comp.config.title || comp.type} (Página: ${pageName})`
                      : `${comp.config.title || `${comp.type} - ${comp.id.substring(0, 8)}`}`;
                    return (
                      <SelectItem key={comp.id} value={comp.id}>
                        {displayName}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                O botão abrirá um formulário com os campos da tabela/lista
                selecionada
              </p>
            </div>
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Editar Componente</DialogTitle>
          <DialogDescription>
            Configure as propriedades do componente selecionado
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {renderFields()}
          </div>

          <DialogFooter className="mt-4 flex-shrink-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
